import {
  AddToCartModal,
  useAddToCartModal,
  getCount,
  getInCart,
} from '@hungryroot/cart';
import { type UseCartMutations } from '@hungryroot/cart';
import { AccountStatus } from '@hungryroot/customer';
import { type DietaryRestrictionsText } from '@hungryroot/dietary-restrictions';
import { WarningText } from '@hungryroot/pho';
import {
  PairingLoader,
  RDPModal as BaseRDPModal,
  XButton,
  PDPModal,
  RecipeBookmarkButton,
  ShareButtonContainer,
} from '@hungryroot/rdp';
import {
  type PairingDetail,
  type ProductDetailList,
  type PairingBundle,
  type PairingV1 as Pairing,
} from '@hungryroot/rest';
import {
  type PairingWithIngredientDetails,
  type EditRecipeOrderDetail,
  type UseEditRecipeResult,
} from '../../../hooks/useEditRecipe/useEditRecipe';
import { useCustomization } from '../../../providers/CustomizationProvider/CustomizationProvider';
import { PairingEdit } from '../../edit-recipe/PairingEdit/PairingEdit';
import { ServingSizeWarningModal } from '../../ServingSizeWarningModal/ServingSizeWarningModal';

import { RDPModalContent } from './components/RDPModalContent';
import { RDPModalFooter } from './components/RDPModalFooter';
import { RDPModalHeader } from './components/RDPModalHeader';
import { useRDPModalAnalytics } from './hooks/useRDPModalAnalytics';
import { useRDPModalCart } from './hooks/useRDPModalCart';
import { useRDPModalModals } from './hooks/useRDPModalModals';
import { useRDPModalViewModel } from './hooks/useRDPModalViewModel';
import { usePdpModal } from './usePdpModal';
export interface RDPModalProps {
  initialPairing: Pairing;
  orderId?: number;
  onDismiss: () => void;
  initialBundle?: {
    order_details: EditRecipeOrderDetail[];
    points: PairingBundle['points'];
  };
  testid?: string;
  index?: number | null;
  onProductContentScroll?: (scrollDepth: number) => void;
}

export interface RDPContentProps {
  pairingV2: PairingDetail | undefined;
  currentPairing: PairingWithIngredientDetails;
  editRecipeQuery: UseEditRecipeResult;
  cartMutations: UseCartMutations | null;
  index: number | null;
  onDismiss: () => void;
  dietaryIngredients: DietaryRestrictionsText[];
  ingredientContainsNeverPeriodicity: boolean;
  initialPairing: Pairing;
  orderId?: number;
  accountStatus?: AccountStatus;
  onProductContentScroll?: (scrollDepth: number) => void;
  testid?: string;
  isLoadingAccountStatus: boolean;
  count: number;
}

export type TriggerInteractionEventArgs = {
  orderId: number;
  id: number;
  name: string;
  interactionType: 'add_to_cart' | 'remove_from_cart';
};

export function RDPModal({
  initialPairing,
  orderId,
  onDismiss,
  initialBundle,
  testid,
  index = null,
  onProductContentScroll,
}: RDPModalProps) {
  const customization = useCustomization();
  const cartMutations =
    'mutations' in customization ? customization.mutations : null;

  const useAddToCartModalQuery = useAddToCartModal();
  const { accountStatus, isLoading: isLoadingAccountStatus } =
    useAddToCartModalQuery;

  let count = 0;
  const inCart =
    'cart' in customization
      ? getInCart(initialPairing.id, true, customization)
      : false;

  if (inCart && 'cart' in customization) {
    count = getCount(initialPairing.id, true, customization);
  }

  const outOfStockOptions =
    customization.state === 'Ready'
      ? {
          inventory_date: customization.order.inventory_date,
          origin: customization.order?.origin ?? undefined,
        }
      : undefined;

  return (
    <PairingEdit
      orderId={orderId}
      initialBundle={initialBundle}
      initialPairing={initialPairing}
    >
      {({
        pairing: currentPairing,
        dietaryIngredients,
        ingredientContainsNeverPeriodicity,
        editRecipeQuery,
      }) => {
        return (
          <PairingLoader
            id={currentPairing.id}
            outOfStockOptions={outOfStockOptions}
          >
            {({ pairing: pairingV2 }) => {
              return (
                <RDPContent
                  pairingV2={pairingV2}
                  currentPairing={currentPairing}
                  editRecipeQuery={editRecipeQuery}
                  cartMutations={cartMutations}
                  index={index}
                  onDismiss={onDismiss}
                  dietaryIngredients={dietaryIngredients}
                  ingredientContainsNeverPeriodicity={
                    ingredientContainsNeverPeriodicity
                  }
                  initialPairing={initialPairing}
                  orderId={orderId}
                  accountStatus={accountStatus}
                  onProductContentScroll={onProductContentScroll}
                  testid={testid}
                  isLoadingAccountStatus={isLoadingAccountStatus}
                  count={count}
                />
              );
            }}
          </PairingLoader>
        );
      }}
    </PairingEdit>
  );
}

const RDPContent = ({
  pairingV2,
  currentPairing,
  editRecipeQuery,
  cartMutations,
  index,
  onDismiss,
  dietaryIngredients,
  ingredientContainsNeverPeriodicity,
  initialPairing,
  orderId,
  onProductContentScroll,
  testid,
  isLoadingAccountStatus,
  count,
  accountStatus,
}: RDPContentProps) => {
  const modals = useRDPModalModals();
  const analytics = useRDPModalAnalytics();
  const cart = useRDPModalCart({
    cartMutations,
    accountStatus,
    currentPairing,
    dietaryIngredients,
    editRecipeQuery,
    onDismiss,
  });
  const logic = useRDPModalViewModel({
    currentPairing,
    pairingV2,
    dietaryIngredients,
    ingredientContainsNeverPeriodicity,
    orderId,
    initialPairing,
  });

  const isSkipped = accountStatus === AccountStatus.Skipped;
  const isCanceled = accountStatus === AccountStatus.Canceled;
  const isPaused = accountStatus === AccountStatus.Paused;
  const isAccountInActive = isSkipped || isCanceled || isPaused;

  const {
    featured_img: featured_img_url,
    name,
    discount,
    discount_percentage,
    points_after_discount,
    points,
    badges,
    ingredients,
    id,
    slug,
  } = currentPairing;

  const isEdited = editRecipeQuery.isEdited;

  const { props: pdpProps, openPdp } = usePdpModal({
    onClose: onDismiss,
  });

  const shareButton = (
    <ShareButtonContainer
      title={name}
      text={name}
      url={`${window.location.origin}/recipes/${slug}/`}
      onShareSuccess={() => analytics.handleShare(id, index)}
      onShareError={analytics.handleShareError}
    />
  );

  const bookmarkButton = (
    <RecipeBookmarkButton
      pairingId={id}
      pairingName={name}
      onBookmark={() => analytics.trackBookmark(id, index)}
    />
  );

  return (
    <>
      {pdpProps?.id && (
        <PDPModal
          {...pdpProps}
          onShareSuccess={() => {
            analytics.handleShare(pdpProps.id, index);
          }}
          onFavorite={() => {
            analytics.trackBookmark(pdpProps.id, index);
          }}
          createFooterButton={
            pdpProps.createFooterButton
              ? (product) => {
                  const onAdd = () => analytics.trackAddToCart(id, index);
                  const onRemove = () =>
                    analytics.trackRemoveFromCart(id, index);
                  return pdpProps?.createFooterButton?.(product, {
                    onAdd,
                    onRemove,
                    onIncrement: onAdd,
                    onSubtract: onRemove,
                  });
                }
              : undefined
          }
          onImageScroll={() => {
            analytics.trackNutritionToggle(id, index, true);
          }}
          onProductContentScroll={() => {
            analytics.trackNutritionToggle(id, index, true);
          }}
          onShowMoreNutrition={(isExpanded) => {
            analytics.trackNutritionToggle(id, index, isExpanded);
          }}
          onBackClick={() => {
            pdpProps.onBackClick?.();
          }}
          showOSNSection={true}
        />
      )}
      <BaseRDPModal
        onProductContentScroll={onProductContentScroll}
        testid={testid ?? 'modal-rdp'}
        header={
          <RDPModalHeader
            name={name}
            slug={slug}
            id={id}
            index={index}
            onDismiss={onDismiss}
            analytics={analytics}
          />
        }
        isEdited={isEdited}
        ariaLabel={name}
        isOpen={true}
        imageUrl={featured_img_url === null ? undefined : featured_img_url}
        title={name}
        discount={discount}
        discountPercentage={discount_percentage}
        pointsAfterDiscount={points_after_discount}
        credits={points}
        ingredients={ingredients.reduce(
          (acc, ingredient) => {
            if (
              editRecipeQuery.bundle.order_details &&
              editRecipeQuery.bundle.order_details.find((p) => {
                return p.product === ingredient.id;
              })
            ) {
              acc.push(ingredient as unknown as ProductDetailList);
              return acc;
            }
            return acc;
          },
          [] as PairingDetail['ingredients']
        )}
        primaryTopIconButton={shareButton}
        warningComponent={
          logic.warning && (
            <WarningText intensity={logic.warning?.warningTextIntensity}>
              {logic.warning?.warningText}
            </WarningText>
          )
        }
        secondaryTopIconButton={bookmarkButton}
        primaryTopRightIconButton={
          <XButton
            onClick={() => {
              onDismiss();
            }}
            testid="rdp-modal-x-button"
          />
        }
        productInfo={logic.productInfo}
        badges={badges}
        onDismiss={onDismiss}
      >
        <RDPModalContent
          pairingV2={pairingV2}
          currentPairing={currentPairing}
          editRecipeQuery={editRecipeQuery}
          onImageClick={(productId) => openPdp({ id: productId })}
          onNutritionToggle={(isExpanded) => {
            analytics.trackNutritionToggle(id, index, isExpanded);
          }}
          isEdited={isEdited}
          isLoadingAccountStatus={isLoadingAccountStatus}
          index={index}
          id={id}
          isAccountInActive={isAccountInActive}
          initialPairing={initialPairing}
          onDismiss={onDismiss}
          modals={{
            isEditOpen: modals.isEditOpen,
            setIsEditOpen: modals.setIsEditOpen,
            isEditRecipeModalOpen: modals.isEditRecipeModalOpen,
            setIsEditRecipeModalOpen: modals.setIsEditRecipeModalOpen,
          }}
        />
        {orderId ? (
          <RDPModalFooter
            orderId={orderId}
            currentPairing={currentPairing}
            editRecipeQuery={editRecipeQuery}
            cartMutations={cartMutations}
            isAccountInActive={isAccountInActive}
            areAllIngredientsInStock={logic.areAllIngredientsInStock ?? true}
            count={count}
            showServingSizeWarningModal={logic.showServingSizeWarningModal}
            initialPairing={initialPairing}
            onDismiss={onDismiss}
            analytics={analytics}
            cart={cart}
            modals={modals}
            index={index}
          />
        ) : null}
      </BaseRDPModal>
      {modals.isServingSizeWarningModalOpen && (
        <ServingSizeWarningModal
          testid="rdp-plan-serving-size-modal"
          heading={`You added a ${currentPairing.servings} serving recipe`}
          onActionClick={() => {
            modals.setIsServingSizeWarningModalOpen(false);
            modals.setIsUpdateCartModalOpen(true);
          }}
          onDismiss={() => {
            modals.setIsServingSizeWarningModalOpen(false);
            modals.setIsUpdateCartModalOpen(true);
          }}
        />
      )}
      {(modals.isEditRecipeModalOpen || modals.isUpdateCartModalOpen) && (
        <AddToCartModal
          initialShowModal={
            modals.isEditRecipeModalOpen || modals.isUpdateCartModalOpen
          }
          accountStatus={accountStatus}
          orderId={orderId}
          onAccountChangeSuccess={async () => {
            if (modals.isEditRecipeModalOpen) {
              modals.setIsEditOpen(true);
            }
          }}
          onCancel={() => {
            modals.setIsEditRecipeModalOpen(false);
            modals.setIsUpdateCartModalOpen(false);
          }}
          unskipAndEdit={modals.isEditRecipeModalOpen}
        />
      )}
    </>
  );
};
